import React, { useState } from "react";
import { _post } from "../../services/apiClient";
import { AccountPayload } from "../../types";

interface AccountInfoProps {
  account: any; // contains data.user
  formAccount: AccountPayload;
  setFormAccount: React.Dispatch<React.SetStateAction<AccountPayload>>;
  onSave: (payload: Partial<AccountPayload>) => Promise<void>;
}

const AccountInfo: React.FC<AccountInfoProps> = ({
  account,
  formAccount,
  setFormAccount,
  onSave,
}) => {
  const [editAccount, setEditAccount] = useState(false);
  const [passwordSwitch, setPasswordSwitch] = useState(false);
  const [passwordWarning, setPasswordWarning] = useState(false);
  const [confirmPasswordWarning, setConfirmPasswordWarning] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange =
    (key: keyof AccountPayload) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormAccount((prev) => ({ ...prev, [key]: e.target.value }));
    };

  const validate = () => {
    let valid = true;

    // password length
    if (passwordSwitch) {
      const len = formAccount.password?.length ?? 0;
      const badLen = len < 8 || len > 25;
      setPasswordWarning(badLen);
      if (badLen) valid = false;

      // confirm match
      const mismatch =
        (formAccount.password || "") !==
        (formAccount.password_confirmation || "");
      setConfirmPasswordWarning(mismatch);
      if (mismatch) valid = false;
    } else {
      setPasswordWarning(false);
      setConfirmPasswordWarning(false);
    }

    return valid;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSubmitting(true);
    try {
        const payload: Partial<AccountPayload> = {
        name: formAccount.name?.trim(),
        email: formAccount.email?.trim(),
        mobile_no: formAccount.mobile_no?.trim(),
        change_password: passwordSwitch,
        ...(passwordSwitch
            ? {
                current_password: formAccount.current_password,
                password: formAccount.password,
                password_confirmation: formAccount.password_confirmation,
            }
            : {}),
        };

        await onSave(payload);   // 🔹 This is where your service call happens

        // clear password fields after success
        if (passwordSwitch) {
            setFormAccount((prev) => ({
                ...prev,
                change_password: true,
                current_password: "",
                password: "",
                password_confirmation: "",
            }));
            setPasswordSwitch(false);
        }

        setEditAccount(false);
    } finally {
        setSubmitting(false);
    }
  };

  const handleCancel = () => {
    // reset fields to original data
    setFormAccount({
      name: account.name || "",
      email: account.email || "",
      mobile_no: account.mobile_no || "",
      change_password: false,
      current_password: "",
      password: "",
      password_confirmation: "",
    });
    setPasswordSwitch(false);
    setPasswordWarning(false);
    setConfirmPasswordWarning(false);
    setEditAccount(false);
  };

  return (
    <div className="mb-4">
      <h4 className="text-secondary">
        Account Info
        <small className="pull-right">
          <span className="edit-icon" style={{ cursor: "pointer" }}>
            {!editAccount ? (
              <i className="bi bi-pencil-square" onClick={() => setEditAccount(true)} />
            ) : (
              <i className="bi bi-x-square bg-danger text-light" onClick={handleCancel} />
            )}
          </span>
        </small>
      </h4>

      {!editAccount ? (
        <div>
          <p><strong>Name:</strong> {account?.name}</p>
          <p><strong>Email:</strong> {account?.email}</p>
          <p><strong>Mobile:</strong> {account?.mobile_no}</p>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <input
            className="form-control mb-2"
            value={formAccount?.name || ""}
            onChange={handleChange("name")}
            placeholder="Name"
            maxLength={50}
            required
          />
          <input
            className="form-control mb-2"
            value={formAccount?.email || ""}
            onChange={handleChange("email")}
            placeholder="Email"
            type="email"
            maxLength={50}
            required
          />
          <input
            className="form-control mb-2"
            value={formAccount?.mobile_no || ""}
            onChange={handleChange("mobile_no")}
            placeholder="Mobile"
            inputMode="numeric"
            maxLength={11}
            pattern="\d{11}"
            required
          />

          {/* Password switch (conditional reset password section) */}
          <div className="form-group mb-3">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="updatePassword"
                checked={passwordSwitch}
                onChange={(e) => setPasswordSwitch(e.target.checked)}
              />
              <label className="form-check-label text-primary" htmlFor="updatePassword">
                <b>Change Password</b>
              </label>
            </div>
          </div>

          {passwordSwitch && (
            <>
              <div className="form-floating mb-3">
                <input
                  type="password"
                  className="form-control"
                  placeholder="Current Password"
                  value={formAccount.current_password || ""}
                  onChange={handleChange("current_password")}
                  required={passwordSwitch}
                />
                <label>Current Password</label>
              </div>

              <div className="form-floating mb-3">
                <input
                  type="password"
                  className="form-control"
                  placeholder="New Password"
                  value={formAccount.password || ""}
                  onChange={handleChange("password")}
                  onBlur={validate}
                  required={passwordSwitch}
                />
                <label>New Password</label>
                {passwordWarning && (
                  <div className="text-danger mt-1">
                    Password length must be greater than 7 and less than 25!
                  </div>
                )}
              </div>

              <div className="form-floating mb-3">
                <input
                  type="password"
                  className="form-control"
                  placeholder="Confirm New  Password"
                  value={formAccount.password_confirmation || ""}
                  onChange={handleChange("password_confirmation")}
                  onBlur={validate}
                  required={passwordSwitch}
                />
                <label>Confirm New  Password</label>
                {confirmPasswordWarning && (
                  <div className="text-danger mt-1">
                    Confirm Password does not matched with New Password
                  </div>
                )}
              </div>
            </>
          )}

          <div className="d-block text-right mt-3">
            <button
              type="button"
              className="btn btn-sm btn-danger me-2"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-sm btn-success"
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Submit"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AccountInfo;